import React, { useState, useEffect, useCallback } from 'react';
import { notesAPI } from '@/lib/api';
import { isKRRole, formatDateTime } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Trash2, Loader2, StickyNote, Lock, Users } from 'lucide-react';

export function NotesTab({ entityType, entityId }) {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [content, setContent] = useState('');
  const [visibility, setVisibility] = useState('kr_only');
  const [saving, setSaving] = useState(false);
  const isKR = isKRRole(user);

  const load = useCallback(async () => {
    try {
      const res = await notesAPI.list(entityType, entityId);
      setNotes(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async () => {
    if (!content.trim()) return;
    setSaving(true);
    try {
      await notesAPI.create(entityType, entityId, { content: content.trim(), visibility });
      toast.success('Note added');
      setContent('');
      setShowForm(false);
      await load();
    } catch (e) {
      toast.error('Failed to add note');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (noteId) => {
    try {
      await notesAPI.delete(noteId);
      toast.success('Note deleted');
      await load();
    } catch (e) {
      toast.error('Failed to delete note');
    }
  };

  if (loading) return <div className="flex justify-center p-8"><Loader2 className="animate-spin" size={24} /></div>;

  return (
    <div className="space-y-4" data-testid="notes-tab">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <StickyNote size={18} className="text-slate-500" />
          <h3 className="text-base font-semibold">Internal Notes</h3>
        </div>
        {(isKR || true) && (
          <Button size="sm" variant="outline" onClick={() => setShowForm(!showForm)} data-testid="add-note-btn">
            <Plus size={14} className="mr-1" /> Add Note
          </Button>
        )}
      </div>

      {showForm && (
        <Card>
          <CardContent className="pt-4 space-y-3">
            <Textarea
              value={content} onChange={e => setContent(e.target.value)}
              placeholder="Write a note..." rows={3}
              data-testid="note-content-input"
            />
            <div className="flex items-center gap-3">
              {isKR && (
                <Select value={visibility} onValueChange={setVisibility}>
                  <SelectTrigger className="w-[180px] h-8 text-xs" data-testid="note-visibility-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="kr_only"><Lock size={12} className="inline mr-1" />KR Only</SelectItem>
                    <SelectItem value="kr_agency"><Users size={12} className="inline mr-1" />KR + Agency</SelectItem>
                  </SelectContent>
                </Select>
              )}
              <div className="flex-1" />
              <Button size="sm" variant="ghost" onClick={() => { setShowForm(false); setContent(''); }}>Cancel</Button>
              <Button size="sm" onClick={handleSubmit} disabled={saving || !content.trim()} data-testid="save-note-btn">
                {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : null} Save
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {notes.length === 0 ? (
        <Card><CardContent className="py-6 text-center text-sm text-slate-500">
          No notes yet. Add the first note to start an internal log.
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {notes.map(note => (
            <Card key={note.note_id} className="border-l-2 border-l-slate-300" data-testid={`note-${note.note_id}`}>
              <CardContent className="py-3 px-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-xs font-medium text-slate-700">{note.author_name}</span>
                      <span className="text-xs text-slate-400">{formatDateTime(note.created_at)}</span>
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                        {note.visibility === 'kr_only' ? <><Lock size={8} className="mr-0.5" /> KR Only</> : <><Users size={8} className="mr-0.5" /> KR + Agency</>}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-600 whitespace-pre-wrap">{note.content}</p>
                  </div>
                  {isKR && (
                    <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-600 shrink-0"
                      onClick={() => handleDelete(note.note_id)} data-testid={`delete-note-${note.note_id}`}>
                      <Trash2 size={14} />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
